import { useEffect, useMemo } from 'react';
import { Redirect, useLocalSearchParams } from 'expo-router';
import { processAppleSurfaceQueryParams, queryStringParam } from '@/services/appleDeepLinkSurface';
import { isAppleReadIntent, resolveAppleReadIntentRoute } from '@/services/appleIntentsAndSpotlight';
import { normalizeTripRouteParam } from '@/utils/tripSelection';

/**
 * Widget / Live Activity taps use tripvio://nav?target=plan&tripId=…&day=…
 * Without this route Expo Router shows "Unmatched Route".
 *
 * Uses `<Redirect>` (render-time) instead of `router.replace` in an effect so cold
 * launches don't sit on an intermediate spinner while auth + fonts initialize.
 */
export default function AppleNavDeepLinkRoute() {
  const params = useLocalSearchParams();
  const target = queryStringParam(params.target);
  const tripId = normalizeTripRouteParam(params.tripId);
  const day = queryStringParam(params.day);
  const itemId = queryStringParam(params.itemId);
  const focus = queryStringParam(params.focus);
  const appIntent = queryStringParam(params.appIntent);
  const appleSurface = queryStringParam(params.appleSurface);

  useEffect(() => {
    processAppleSurfaceQueryParams(params as Record<string, unknown>);
  }, [appIntent, appleSurface, day, focus, itemId, target, tripId]);

  const href = useMemo(() => {
    if (isAppleReadIntent(appIntent)) {
      return resolveAppleReadIntentRoute(appIntent, { tripId, day }) as never;
    }

    if (target === 'plan') {
      const routeParams: Record<string, string> = {};
      if (tripId) {
        routeParams.tripId = tripId;
      }
      if (day) {
        routeParams.day = day;
      }
      if (itemId) {
        routeParams.itemId = itemId;
      }
      if (focus) {
        routeParams.focus = focus;
      }
      if (appIntent) {
        routeParams.appIntent = appIntent;
      }
      if (appleSurface) {
        routeParams.appleSurface = appleSurface;
      }
      return {
        pathname: '/(tabs)/plan',
        params: routeParams,
      } as never;
    }

    if (target === 'trips') {
      return '/(tabs)/' as never;
    }

    return '/(tabs)/' as never;
  }, [appIntent, appleSurface, day, focus, itemId, target, tripId]);

  return <Redirect href={href} />;
}
